import { useState, useEffect, useCallback, useMemo } from 'react'
import { Outlet } from 'react-router-dom'
import { usePageVisibilityRefresh } from '~/api/use-page-visibility-refresh'
import { Box, useMediaQuery } from '@mui/material'
import AppBarComponent from './appbar'
import Sidebar from './sidebar'
import {
  DESKTOP_DRAWER_WIDTH,
  COLLAPSED_DESKTOP_DRAWER_WIDTH,
  CONTENT_PADDING_REM,
  APPBAR_HEIGHT_REM,
  DESKTOP_BREAKPOINT,
  APPBAR_EXTRA_DESKTOP_REM
} from '../common/constants/layout-constants'
import { OrgContextProvider } from '../common/context/organization/org-context-provider'
import { CropsContextProvider } from '../common/context/crops/crops-context-provider'
import { UserPreferencesContextProvider } from '../common/context/user-preferences/user-preferences-context-provider'
import { SidebarContext } from '../common/context/sidebar/sidebar-context'
import { SnackbarProvider } from 'notistack'
import { StyledSnackbar } from './snackbar'
import theme from '~/styles/mui-theme'

function ProtectedLayout() {
  usePageVisibilityRefresh()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed')
    return saved ? JSON.parse(saved) : false
  })
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_BREAKPOINT))

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed))
  }, [collapsed])

  const handleDrawerToggle = useCallback(() => setMobileOpen((prev) => !prev), [])
  const handleCollapsedToggle = useCallback(() => setCollapsed((prev) => !prev), [])

  const currentDrawerWidth = collapsed ? COLLAPSED_DESKTOP_DRAWER_WIDTH : DESKTOP_DRAWER_WIDTH

  const sidebarContextValue = useMemo(
    () => ({ sidebarWidth: isDesktop ? currentDrawerWidth : 0 }),
    [isDesktop, currentDrawerWidth],
  )

  return (
    <SidebarContext.Provider value={sidebarContextValue}>
    <UserPreferencesContextProvider>
    <OrgContextProvider>
      <CropsContextProvider>
        <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={4000}
        Components={{
          success: StyledSnackbar,
          error: StyledSnackbar,
          warning: StyledSnackbar,
          info: StyledSnackbar,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <AppBarComponent onMenuClick={handleDrawerToggle} />
          <Box sx={{ display: 'flex', flexGrow: 1 }}>
            <Sidebar
              mobileOpen={mobileOpen}
              onClose={handleDrawerToggle}
              collapsed={collapsed}
              onCollapsedToggle={handleCollapsedToggle}
            />
            <Box
              component="main"
              sx={{
                flexGrow: 1,
                minWidth: 0,
                p: CONTENT_PADDING_REM,
                mt: isDesktop ? APPBAR_HEIGHT_REM : `calc(${APPBAR_HEIGHT_REM} - ${APPBAR_EXTRA_DESKTOP_REM})`,
              }}
            >
              <Outlet />
            </Box>
          </Box>
        </Box>
      </SnackbarProvider>
      </CropsContextProvider>
    </OrgContextProvider>
    </UserPreferencesContextProvider>
    </SidebarContext.Provider>
  )
}

export default ProtectedLayout
